import styles from '../../assets/scss/components/collections/collections.module.scss'
import { FC, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Image, Link, useUrl } from '@shopify/hydrogen'
import { EnhancedMenuItem } from '../../lib/menus'
import { classNames } from '@shopify/css-utilities'
import { useI18n } from '../i18n.client'
import { HeaderContext } from '../header/header-provider.client'
import { Swiper, SwiperSlide, SwiperClass } from 'swiper/react'
import { FreeMode, Navigation } from 'swiper/modules'
import 'swiper/scss'
import 'swiper/scss/navigation'

export const ProductMenu: React.FC<{
  activeFilterCount?: number
  menu?: EnhancedMenuItem
}> = ({ activeFilterCount, menu }) => {
  const [i18n] = useI18n()
  const { pathname } = useUrl()
  const { setIsFilterDrawerOpen } = useContext(HeaderContext)
  const [swiperRef, setSwiperRef] = useState<SwiperClass | null>(null)
  const navigationPrevRef = useRef<HTMLDivElement>(null)
  const navigationNextRef = useRef<HTMLDivElement>(null)
  const activeIndex = menu?.items.findIndex(({ to }) => pathname.includes(to)) ?? 0

  const handlePrevClick = useCallback(() => {
    swiperRef?.slidePrev()
  }, [swiperRef])

  const handleNextClick = useCallback(() => {
    swiperRef?.slideNext()
  }, [swiperRef])

  if (!menu || !menu.items || !Array.isArray(menu.items) || menu.items.length === 0) {
    return null
  }

  useEffect(() => {
    if (
      swiperRef &&
      navigationPrevRef.current &&
      navigationNextRef.current &&
      swiperRef.navigation
    ) {
      swiperRef.params.navigation.prevEl = navigationPrevRef.current
      swiperRef.params.navigation.nextEl = navigationNextRef.current
      swiperRef.navigation.init()
      swiperRef.navigation.update()
      if (activeIndex >= 0) {
        swiperRef.slideTo(activeIndex)
      }
    }
  }, [swiperRef, activeIndex])

  useEffect(() => {
    const handleResize = () => {
      if (swiperRef) {
        try {
          if (swiperRef.slides && swiperRef.slides.length > 0) {
            swiperRef.updateSlides()
            swiperRef.update()
            swiperRef.navigation && swiperRef.navigation.update()
          }
        } catch (error) {
          console.error(error)
        }
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [swiperRef])

  useEffect(() => {
    if (swiperRef) {
      try {
        if (swiperRef.slides && swiperRef.slides.length > 0) {
          swiperRef.updateSlides()
          swiperRef.update()
          swiperRef.navigation && swiperRef.navigation.update()
        }
      } catch (error) {
        console.error(error)
      }
    }
  }, [menu, swiperRef])

  return (
    <section className={classNames('section', 'px', styles.menuContainer)}>
      <div className={styles.filterContainer}>
        <div className={styles.filterButton} onClick={() => setIsFilterDrawerOpen(true)}>
          {i18n.translate('filters')}
          {activeFilterCount ? ` (${activeFilterCount})` : ''}
        </div>
      </div>
      <div className={styles.menu}>
        <div className={styles.navigationPrev} onClick={handlePrevClick} ref={navigationPrevRef}>
          <Image alt={i18n.translate('previous')} height={8} src="/carousel-arrow.svg" width={27} />
        </div>
        <Swiper
          className={styles.swiper}
          freeMode
          modules={[FreeMode, Navigation]}
          navigation={{
            prevEl: navigationPrevRef.current,
            nextEl: navigationNextRef.current,
          }}
          onSwiper={setSwiperRef}
          slidesPerGroup={1}
          slidesPerView="auto"
          spaceBetween={20}>
          {menu.items.map((item) => (
            <MenuItem item={item} key={item.id} />
          ))}
        </Swiper>
        <div className={styles.navigationNext} onClick={handleNextClick} ref={navigationNextRef}>
          <Image alt={i18n.translate('next')} height={8} src="/carousel-arrow.svg" width={27} />
        </div>
      </div>
    </section>
  )
}

const MenuItem: React.FC<{ item: EnhancedMenuItem }> = ({ item }) => {
  const { pathname } = useUrl()
  const ref = useRef<HTMLAnchorElement>(null)
  const [width, setWidth] = useState(0)

  useEffect(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect()
      setWidth(rect.width)
    }
  }, [])

  return (
    <SwiperSlide className={styles.linkContainer} style={{ width: `${width}px` }}>
      <Link className={classNames(styles.link, pathname.includes(item.to) && styles.active)} ref={ref} to={item.to}>
        {item.title}
      </Link>
    </SwiperSlide>
  )
}

MenuItem.displayName = 'SwiperSlide'
